import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, RadioGroup } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface MetadataItem {
  id: string;
  title: string;
  outcome: string;
  effort: string;
  impact: string;
  confidence: string;
  risk: string;
  timeWindow: string;
  dependencies: string;
}

export default function MinimalMetadataPage() {
  const [items, setItems] = useState<MetadataItem[]>([
    { id: '1', title: '', outcome: '', effort: '', impact: '', confidence: '', risk: '', timeWindow: '', dependencies: '' },
  ]);

  const updateItem = (id: string, field: keyof MetadataItem, value: string) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems([...items, { id: Date.now().toString(), title: '', outcome: '', effort: '', impact: '', confidence: '', risk: '', timeWindow: '', dependencies: '' }]);
  };

  return (
    <TaskPage
      phase="gather"
      title="Minimal Metadata"
      pageNumber={3}
      instruction="Add minimal metadata so we can sort later. Pick gut-feel S/M/L—precision comes later. 'Outcome it serves' = one of your Engage outcomes."
      whyItMatters="One page = one task; compact fields reduce split attention and clutter."
      coachRail={{
        timing: "10 minutes",
        tips: "Pick gut-feel S/M/L—precision comes later. Keep it simple.",
        debrief: "Do we have enough metadata to sort? What's missing?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6 overflow-x-auto">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Metadata Table</h3>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[var(--hover-tint)] border-b-2 border-[var(--rule)]">
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Title</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Outcome</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Effort</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Impact</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Confidence</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Risk</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Time-window</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Dependencies</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-[var(--rule)]">
                  <td className="p-2">
                    <TextInput
                      value={item.title}
                      onChange={(e) => updateItem(item.id, 'title', e.target.value)}
                      placeholder="Title"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.outcome}
                      onChange={(e) => updateItem(item.id, 'outcome', e.target.value)}
                      placeholder="Outcome it serves"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'S', label: 'S' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.effort}
                      onChange={(value) => updateItem(item.id, 'effort', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'S', label: 'S' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.impact}
                      onChange={(value) => updateItem(item.id, 'impact', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'H', label: 'H' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.confidence}
                      onChange={(value) => updateItem(item.id, 'confidence', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'H', label: 'H' },
                        { value: 'M', label: 'M' },
                        { value: 'L', label: 'L' }
                      ]}
                      value={item.risk}
                      onChange={(value) => updateItem(item.id, 'risk', value)}
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'now', label: 'Now' },
                        { value: 'next', label: 'Next' },
                        { value: 'later', label: 'Later' }
                      ]}
                      value={item.timeWindow}
                      onChange={(value) => updateItem(item.id, 'timeWindow', value)}
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.dependencies}
                      onChange={(e) => updateItem(item.id, 'dependencies', e.target.value)}
                      placeholder="Dependencies"
                      className="text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            onClick={addItem}
            className="mt-4 w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
          >
            + Add another item
          </button>
        </div>

        <Callout type="tip" phase="gather">
          <p className="text-sm">S/M/L = Small/Medium/Large. H/M/L = High/Medium/Low. Pick gut-feel—precision comes later.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






